import React, { useState } from 'react';
import { isAfter } from 'date-fns';

import { Alert, Box, DateInput, DatePicker, FormField, Label } from '@zillow/constellation-10';

import { coerceToDate } from '@common/paymentsUtils/dateUtils';
import { getLastSelectablePaymentDate } from '@common/paymentsUtils/dateUtils';

interface Props {
  label: string;
  dueDate: string;
  selectedDate: string;
  changeSelectedDate: (selectedDate: string) => void;
}

const latePaymentText =
  'This is after the payment date your landlord chose. Make sure they are okay with getting the rent later.';

const ManualSchedulingPage = ({ label, dueDate, selectedDate, changeSelectedDate }: Props) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const isPaymentDateAfterDueDate = () => {
    const left = coerceToDate(selectedDate);
    const right = coerceToDate(dueDate);
    return !!left && !!right && isAfter(left, right);
  };

  const getLastSelectableDate = getLastSelectablePaymentDate(dueDate);
  const minDate = new Date();
  minDate.setDate(new Date().getDate() - 1);

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      <FormField
        css={{ width: '100%' }}
        label={<Label size="lg">{label}</Label>}
        control={
          <DatePicker
            closeModalOnDateSelect
            dateFormat="MM/dd/yyyy"
            disabledDates={(date: Date) => date < minDate || date > getLastSelectableDate}
            onOpenChange={(open) => setIsCalendarOpen(open)}
            open={isCalendarOpen}
            input={
              <DateInput
                css={{ paddingInlineStart: 'default' }}
                value={selectedDate}
                readOnly
                onClick={() => setIsCalendarOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setIsCalendarOpen(true);
                  }
                }}
              />
            }
            onValueChange={(value) => changeSelectedDate(String(value))}
            value={selectedDate}
          />
        }
      />
      {isPaymentDateAfterDueDate() && <Alert tone="warning">{latePaymentText}</Alert>}
      <Alert tone="info">
        We’ll email you a reminder 5 days before your payment is due each month. If this date falls on a weekend or
        holiday, we’ll send the rent 1 business day later.
      </Alert>
    </Box>
  );
};

export default ManualSchedulingPage;
